import { Box, Checkbox } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { PaloModal } from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { Invoice } from '../models/invoice';
import { localBlocker, totalsByCurrency, zeroValued } from './invoice-completion';

const c = tokens.color;

export interface PaloCompleteInvoicesDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (ids: number[]) => void;
    /** Everything the user ticked, completable or not. */
    selectedInvoices: Invoice[];
    busy?: boolean;
}

interface SumRowProps {
    label: string;
    value: ReactElement | string;
    muted?: boolean;
    strong?: boolean;
}

const SumRow = ({ label, value, muted, strong }: SumRowProps): ReactElement => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '11px',
            padding: '5px 0',
            borderBottom: `1px solid ${c.lineSoft}`,
            '&:last-of-type': { borderBottom: 0 },
            fontSize: '12.5px',
            color: muted ? c.ink3 : c.ink,
        }}
    >
        <Box component="span" sx={{ fontWeight: strong ? 620 : 400, minWidth: 0, flex: '1 1 0' }}>
            {label}
        </Box>
        <Box
            component="span"
            sx={{
                fontVariantNumeric: 'tabular-nums',
                fontWeight: strong ? 620 : 400,
                whiteSpace: 'nowrap',
            }}
        >
            {value}
        </Box>
    </Box>
);

/**
 * The confirmation in front of a batch completion (#2136).
 *
 * Follows the shape the CH branch had — a sum table over what is about to be
 * completed — because the number is the thing worth checking before invoice
 * numbers are handed out and the documents stop being editable.
 *
 * What it adds is the other half: which of the ticked invoices will not be
 * completed, and why. The CH dialog silently narrowed the selection, so a user
 * who ticked eight and got five never learned about the three.
 */
export const PaloCompleteInvoicesDialog = ({
    open,
    onClose,
    onConfirm,
    selectedInvoices,
    busy,
}: PaloCompleteInvoicesDialogProps): ReactElement => {
    const completable = selectedInvoices.filter(invoice => undefined === localBlocker(invoice));
    const blocked = selectedInvoices.filter(invoice => undefined !== localBlocker(invoice));

    const totals = totalsByCurrency(completable);
    const zero = zeroValued(completable);

    // Asked again every time the dialog opens: a tick carried over from the
    // last batch would be a tick nobody gave for this one.
    const [zeroAcknowledged, setZeroAcknowledged] = useState(false);

    useEffect(() => {
        if (open) {
            setZeroAcknowledged(false);
        }
    }, [open]);

    const blockedByZero = zero.length > 0 && !zeroAcknowledged;

    return (
        <PaloModal
            isVisible={open}
            handleClose={onClose}
            title={<FormattedMessage id="app.invoices.complete.title" />}
            width="sm"
            themeContent={true}
            actionChildren={
                <>
                    <Box
                        component="button"
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        sx={{
                            height: 30,
                            padding: '0 13px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            color: c.ink2,
                            backgroundColor: c.surface,
                            border: `1px solid ${c.lineHard}`,
                            borderRadius: tokens.radius.small + 'px',
                        }}
                    >
                        <FormattedMessage id="app.common.cancel" />
                    </Box>
                    <Box
                        component="button"
                        type="button"
                        disabled={busy || 0 === completable.length || blockedByZero}
                        onClick={() => onConfirm(completable.map(invoice => invoice.id))}
                        sx={{
                            height: 30,
                            padding: '0 13px',
                            fontSize: '12px',
                            fontWeight: 540,
                            cursor: 'pointer',
                            color: c.inkInvert,
                            backgroundColor: c.accent,
                            border: `1px solid ${c.accent}`,
                            borderRadius: tokens.radius.small + 'px',
                            '&:hover': { backgroundColor: c.accentHi },
                            '&:disabled': { opacity: 0.5, cursor: 'default' },
                        }}
                    >
                        <FormattedMessage id="app.invoices.complete" values={{ count: completable.length }} />
                    </Box>
                </>
            }
        >
            <Box sx={{ fontSize: '13px', lineHeight: '20px', color: c.ink }}>
                <FormattedMessage id="app.invoices.complete.completeWarning" values={{ count: completable.length }} />
            </Box>

            {completable.length > 0 && (
                <Box sx={{ paddingTop: '11px' }}>
                    {completable.map(invoice => (
                        <SumRow
                            key={invoice.id}
                            label={invoice.recipientAccount?.name ?? String(invoice.id)}
                            value={
                                <FormattedNumber
                                    value={invoice.totalPrice}
                                    style="currency"
                                    currency={invoice.currency}
                                    minimumFractionDigits={2}
                                    maximumFractionDigits={2}
                                />
                            }
                        />
                    ))}
                    {Object.entries(totals).map(([currency, total]) => (
                        <SumRow
                            key={currency}
                            strong={true}
                            label=""
                            value={
                                <FormattedNumber
                                    value={total}
                                    style="currency"
                                    currency={currency}
                                    minimumFractionDigits={2}
                                    maximumFractionDigits={2}
                                />
                            }
                        />
                    ))}
                </Box>
            )}

            {zero.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '6px',
                        marginTop: '12px',
                        padding: '9px 11px 9px 7px',
                        borderRadius: tokens.radius.small + 'px',
                        border: `1px solid ${c.warnLine}`,
                        backgroundColor: c.warnSoft,
                        fontSize: '12.5px',
                        lineHeight: '18px',
                        color: c.ink,
                    }}
                >
                    <Checkbox
                        size="small"
                        checked={zeroAcknowledged}
                        onChange={event => setZeroAcknowledged(event.target.checked)}
                        sx={{ padding: '1px', marginTop: '-1px' }}
                    />
                    <Box
                        component="label"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setZeroAcknowledged(!zeroAcknowledged)}
                    >
                        <FormattedMessage id="app.invoices.complete.zeroValue" values={{ count: zero.length }} />
                    </Box>
                </Box>
            )}

            {blocked.length > 0 && (
                <Box sx={{ paddingTop: '14px' }}>
                    <Box
                        sx={{
                            fontSize: '10.5px',
                            fontWeight: 640,
                            letterSpacing: '.04em',
                            textTransform: 'uppercase',
                            color: c.ink3,
                            paddingBottom: '3px',
                        }}
                    >
                        <FormattedMessage id="app.invoices.complete.skippedTitle" values={{ count: blocked.length }} />
                    </Box>
                    {blocked.map(invoice => (
                        <SumRow
                            key={invoice.id}
                            muted={true}
                            label={invoice.number || (invoice.recipientAccount?.name ?? String(invoice.id))}
                            value={<FormattedMessage id={`app.invoices.complete.skipped.${localBlocker(invoice)}`} />}
                        />
                    ))}
                </Box>
            )}
        </PaloModal>
    );
};
